import { setActiveTeamIdForRequests } from "@frontend/utils/active_team_header.client";
import type { UserAppBackendOutputs } from "@frontend/utils/orpc.client";
import { orpc } from "@frontend/utils/orpc.tanstack.client";
import { syncPushIfGranted } from "@frontend/utils/push.utils";
import { requestFreshSession } from "@frontend/utils/session_refresh.client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, type ReactNode, useContext, useEffect } from "react";
import { useRevalidator } from "react-router";
import { toast } from "react-toastify";
import type { SessionInfo } from "./UserContext";

export type Team = UserAppBackendOutputs["teams"]["getMyTeams"][number];

// Every user always has an active team (personal on signup), so
// `activeWorkspace` is never undefined once the session is loaded.
export interface Workspace {
	id: string;
	name: string;
	// A workspace is "personal" iff it is the user's personal team on the
	// server (row where personalTeamForUserId === user.id).
	type: "personal" | "team";
	role: Team["userRole"];
}

interface WorkspaceContextType {
	activeWorkspace: Workspace;
	teams: Team[];
	isLoading: boolean;
	user: SessionInfo["user"];
	setActiveTeam: (teamAppId: string) => Promise<void>;
	isSwitching: boolean;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

interface WorkspaceProviderProps {
	children: ReactNode;
	sessionInfo?: SessionInfo | null;
}

// Fallback for the brief window between mount and getMyTeams resolving.
// Renders as a neutral chip; nothing team-scoped is fetched until teams
// arrive because the `x-team-id` header cache is empty.
const PLACEHOLDER_WORKSPACE: Workspace = {
	id: "",
	name: "Loading…",
	type: "personal",
	role: "Owner",
};

export function WorkspaceProvider({ children, sessionInfo }: WorkspaceProviderProps) {
	const queryClient = useQueryClient();
	const revalidator = useRevalidator();
	const userId = sessionInfo?.user?.id;
	const activeTeamAppId = sessionInfo?.user?.activeTeamAppId ?? null;

	const { data: teams = [], isLoading } = useQuery({
		...orpc.teams.getMyTeams.queryOptions({}),
		enabled: !!userId,
	});

	// Keep the `x-team-id` header cache in step with the loader-derived
	// active team (the loader seeds it first; this covers revalidations).
	useEffect(() => {
		setActiveTeamIdForRequests(activeTeamAppId);
	}, [activeTeamAppId]);

	// Silent FCM registration on login. NEVER prompts for permission —
	// browsers permanently disable Notification.requestPermission() for
	// the origin if the user dismisses a bad prompt once. The user-gesture
	// version (promptAndRegisterPush) lives behind explicit UI: the
	// post-first-entry modal, the profile toggle, or the dashboard banner.
	useEffect(() => {
		if (!userId) return;
		void syncPushIfGranted();
	}, [userId]);

	const setActiveTeamMutation = useMutation(
		orpc.teams.setActiveTeam.mutationOptions({
			onSuccess: async ({ activeTeamAppId: newId }) => {
				// 1. Flip the header cache so every subsequent RPC carries the
				//    new team id.
				setActiveTeamIdForRequests(newId);
				// 2. Re-run the router's authLoader so `sessionInfo.user.
				//    activeTeamAppId` (fed via useLoaderData) reflects the new
				//    value for every consumer (activeWorkspace, enabled-gates).
				//
				//    `setActiveTeam` writes `users.activeTeamAppId` with a plain
				//    db update, which better-auth's 5-minute session cookie cache
				//    knows nothing about. Left alone, the loader reads the stale
				//    cookie, returns the OLD team and re-seeds the header cache
				//    with it — the switch reverts and nothing throws, which the
				//    user sees as "clicking the team did nothing". Ask the loader
				//    for one uncached read.
				requestFreshSession();
				revalidator.revalidate();
				// 3. Every cached query was fetched under the old team — drop
				//    the lot so nothing from team A renders under team B.
				await queryClient.invalidateQueries();
			},
			onError: (err) => {
				toast.error(err.message || "Failed to switch team");
			},
		}),
	);

	const activeWorkspace: Workspace = (() => {
		if (!activeTeamAppId || teams.length === 0) return PLACEHOLDER_WORKSPACE;
		const active = teams.find((t) => t.id === activeTeamAppId);
		if (!active) return PLACEHOLDER_WORKSPACE;
		return {
			id: active.id,
			name: active.name,
			type: active.personalTeamForUserId === userId ? "personal" : "team",
			role: active.userRole,
		};
	})();

	return (
		<WorkspaceContext.Provider
			value={{
				activeWorkspace,
				teams,
				isLoading: isLoading && teams.length === 0,
				user: sessionInfo?.user || null,
				setActiveTeam: async (teamAppId) => {
					await setActiveTeamMutation.mutateAsync({ teamAppId });
				},
				isSwitching: setActiveTeamMutation.isPending,
			}}
		>
			{children}
		</WorkspaceContext.Provider>
	);
}

export const useWorkspace = () => {
	const context = useContext(WorkspaceContext);
	if (context === undefined) {
		throw new Error("useWorkspace must be used within a WorkspaceProvider");
	}
	return context;
};

// Returns the id of the active team, or `null` while it's loading. Callers
// that must have a team id (team-scoped queries) should gate on this.
export const useActiveTeamId = () => {
	const { activeWorkspace } = useWorkspace();
	return activeWorkspace.id || null;
};
